/**
 * Shared date-display formatting. Previously every component rolled its own
 * `new Date(x).toLocaleDateString(...)` call - some with `"en-GB"` (DD/MM/YYYY, slashes), some
 * with no locale at all (format follows the visiting browser/OS, unpredictable), one with raw
 * ISO (`YYYY-MM-DD`). This is the single place that decides how a date is shown to the user:
 * DD-MM-YYYY (hyphens), consistently, everywhere.
 *
 * This only affects DISPLAY. Stored/backend values stay whatever unambiguous format they
 * already use (ISO `YYYY-MM-DD`) - see `taskLogic.js` for date PARSING (free-text input).
 */

/**
 * Format a date for display as "DD-MM-YYYY".
 * @param {Date|string|number|null|undefined} value
 * @returns {string|null} "DD-MM-YYYY", or null if `value` is empty/unparseable (caller decides
 *   the fallback text, e.g. "N/A", "—", matching existing per-site conventions).
 */
const formatDisplayDate = (value) => {
  if (!value) return null;

  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Any value as shown to the user, for fields that hold a date or free text (a Time Interval,
 * Field Phase): a date - a Date, "YYYY-MM-DD" or an ISO datetime from the API - as "DD-MM-YYYY",
 * read as written (no time-zone shift); anything else ("7 DAA", "ultimo December 2026",
 * "BBCH 89") unchanged. Empty gives "".
 * @param {Date|string|number|null|undefined} value
 * @returns {string}
 */
const formatDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) return formatDisplayDate(value) ?? "";
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const written = writtenDate(text);
  return written ?? text;
};

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// A full date written out - "20th December 2024", "December 20, 2024", "20/12/2024",
// "20.12.2024" - as "DD-MM-YYYY"; null for anything else, e.g. "ultimo December 2026" (no day)
const writtenDate = (text) => {
  const pad = (n) => String(n).padStart(2, "0");
  const build = (day, monthIndex, year) => {
    const d = new Date(year, monthIndex, day);
    return d.getMonth() === monthIndex && d.getDate() === day ? `${pad(day)}-${pad(monthIndex + 1)}-${year}` : null;
  };
  const monthOf = (name) => MONTHS.indexOf(name.slice(0, 3).toLowerCase());
  let m = text.match(/^(\d{1,2})(?:st|nd|rd|th)?\.?\s+([A-Za-z]{3,})\.?,?\s+(\d{4})$/);
  if (m && monthOf(m[2]) >= 0) return build(Number(m[1]), monthOf(m[2]), Number(m[3]));
  m = text.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/);
  if (m && monthOf(m[1]) >= 0) return build(Number(m[2]), monthOf(m[1]), Number(m[3]));
  m = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) return build(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
};

/**
 * Format a date for display as "DD-MM-YYYY HH:mm" - for datetime values (e.g. session
 * timestamps) where the time of day is meaningful, not just the calendar date.
 * @param {Date|string|number|null|undefined} value
 * @returns {string|null}
 */
const formatDisplayDateTime = (value) => {
  if (!value) return null;

  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;

  const datePart = formatDisplayDate(d);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${datePart} ${hours}:${minutes}`;
};

/**
 * Convert a form/API date value to a local-midnight Date (what `DatePicker.jsx`'s `Calendar`
 * expects), and back (`formatDateToYMD`) to a "yyyy-MM-dd" string. The strict "yyyy-MM-dd" case
 * is handled manually (not via `new Date(string)`/`toISOString()`) to avoid UTC-shift off-by-one
 * bugs near midnight; anything else (e.g. a full ISO datetime straight from the API) falls back
 * to generic `Date` parsing, extracting the calendar date in local time.
 */
const parseYMDToLocalDate = (value) => {
  if (!value) return undefined;

  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  }

  const d = new Date(value);
  if (isNaN(d.getTime())) return undefined;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatDateToYMD = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Single source of truth for the year range used by the four "timing" (future due-date) modes -
 * Exact date, Month position, Week numbers, Seasons - so they can't drift apart from each other
 * again. One year back (a task set up near year-end can still be dated into the tail of the
 * previous year) through three years forward. Deliberately NOT used by "Done date" or Field
 * Phase Start/End, which stay on the date picker's own default range - see `DatePicker.jsx`.
 */
const TIMING_YEAR_START = new Date().getFullYear() - 1;
const TIMING_YEAR_END = new Date().getFullYear() + 3;
const TIMING_YEAR_OPTIONS = Array.from(
  { length: TIMING_YEAR_END - TIMING_YEAR_START + 1 },
  (_, i) => TIMING_YEAR_START + i
);

export {
  formatDate,
  formatDisplayDate,
  formatDisplayDateTime,
  parseYMDToLocalDate,
  formatDateToYMD,
  TIMING_YEAR_START,
  TIMING_YEAR_END,
  TIMING_YEAR_OPTIONS,
};
